/* eslint-disable @typescript-eslint/no-explicit-any */
import { API_BASE_URL } from '../config/api';

// Types
export interface Portfolio {
  id: string;
  name: string;
  description?: string;
  user_id?: string;
  created: string;
  updated: string;
  properties: Property[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shares?: any[];
}

export interface Property {
  id: string;
  address: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  analysisData: any;
  addedDate: string;
  notes?: string;
}

export interface User {
  id: string;
  email: string;
  full_name?: string;
  is_admin: boolean;
  auth_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CacheEntry {
  id: string;
  cache_key: string;
  provider: string;
  data_type: string;
  address: string;
  created_at: string;
  expires_at: string;
}

// ============================================================================
// Sourcing pipeline (mirrors backend/src/services/sourcingService.ts)
// ============================================================================

export interface SourcingScoreComponent {
  signal: string;
  points: number;
  reason: string;
  observation_ids?: string[];
}

export interface SourcingLead {
  id: string;
  user_id: string;
  identity_key: string;
  municipality: string;
  state: string;
  parcel_number: string | null;
  address_line1: string;
  address_unit: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
  first_seen_at: string;
  last_seen_at: string;
  freshness: 'current' | 'stale';
  score: number | null;
  score_version: string | null;
  score_components: SourcingScoreComponent[];
  scored_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SourcingLeadListItem extends SourcingLead {
  source_count: number;
  source_tiers: string[];
  source_types: string[];
}

export interface SourcingSource {
  id: string;
  user_id: string;
  name: string;
  adapter_key: string;
  municipality: string;
  state: string;
  source_type: string;
  source_tier: string;
  public_url: string;
  coverage_mode: 'full_snapshot' | 'incremental';
  is_active: boolean;
  terms_reviewed_at: string | null;
  terms_review_outcome: string | null;
  created_at: string;
  updated_at: string;
}

export interface SourcingImportRun {
  id: string;
  user_id: string;
  source_id: string;
  run_key: string;
  started_at: string;
  completed_at: string | null;
  status: 'running' | 'success' | 'failed';
  coverage_mode: 'full_snapshot' | 'incremental';
  records_seen: number;
  records_accepted: number;
  records_rejected: number;
  artifact_path: string | null;
  artifact_checksum: string | null;
  source_published_at: string | null;
  error_summary: string | null;
  created_at: string;
  updated_at: string;
}

export interface SourcingObservationEvidence {
  id: string;
  user_id: string;
  source_id: string;
  import_run_id: string;
  lead_id: string | null;
  source_record_key: string;
  raw_payload: Record<string, unknown>;
  raw_address: string;
  raw_owner_name: string | null;
  parcel_number: string | null;
  amount_owed: number | null;
  observed_at: string;
  published_at: string | null;
  created_at: string;
  run: SourcingImportRun | null;
  source: SourcingSource | null;
}

export interface SourcingLeadDetail {
  lead: SourcingLead;
  observations: SourcingObservationEvidence[];
}

export interface SourcingLeadAnalysis {
  id: string;
  user_id: string;
  lead_id: string;
  analysis_data: Record<string, unknown>;
  analyzed_address: string;
  created_at: string;
  updated_at: string;
}

export interface SourceWithLatestRun extends SourcingSource {
  latest_run: SourcingImportRun | null;
}

export interface SourcingSourcesOverview {
  sources: SourceWithLatestRun[];
  degraded_municipalities: string[];
  stale_window_days: number;
}

export interface SourcingLeadFilters {
  freshness?: 'current' | 'stale' | 'all';
  tier?: string;
  municipality?: string;
  minScore?: number;
}

export interface Transaction {
  id?: string;
  user_id: string;
  transaction_date: string; // ISO date string (YYYY-MM-DD)
  description: string;
  amount: number; // Positive for income, negative for expense
  vendor_name?: string | null;
  category?: string | null;
  source?: 'manual' | 'csv' | 'bank' | 'ocr';
  status?: 'pending' | 'classified' | 'approved' | 'rejected';
  confidence_score?: number | null;
  notes?: string | null;
  raw_data?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

export interface TransactionAllocation {
  id?: string;
  transaction_id: string;
  property_id: string;
  amount: number;
  percentage?: number | null;
  allocation_method?: 'manual' | 'percentage' | 'per_unit' | 'sqft' | 'custom';
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AllocationTemplate {
  id?: string;
  user_id: string;
  name: string;
  description?: string | null;
  rules: {
    properties: Array<{
      id: string;
      percentage: number;
    }>;
    method: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface ProfitLossReport {
  startDate: string;
  endDate: string;
  propertyId?: string;
  propertyName?: string;
  groupBy: 'month' | 'quarter' | 'year';
  periods: Array<{
    period: string;
    income: number;
    expenses: number;
    net: number;
    incomeByCategory: Record<string, number>;
    expensesByCategory: Record<string, number>;
  }>;
  totals: {
    income: number;
    expenses: number;
    net: number;
  };
}

export interface CashFlowReport {
  startDate: string;
  endDate: string;
  propertyId?: string;
  propertyName?: string;
  groupBy: 'month' | 'quarter' | 'year';
  periods: Array<{
    period: string;
    cashIn: number;
    cashOut: number;
    netCashFlow: number;
  }>;
  totals: {
    cashIn: number;
    cashOut: number;
    netCashFlow: number;
  };
}

export interface ScheduleEReport {
  year: number;
  propertyId: string;
  propertyAddress: string;
  income: {
    rentalIncome: number;
    otherIncome: number;
    total: number;
  };
  expenses: {
    advertising: number;
    auto: number;
    cleaning: number;
    commissions: number;
    insurance: number;
    legal: number;
    management: number;
    mortgageInterest: number;
    otherInterest: number;
    repairs: number;
    supplies: number;
    taxes: number;
    utilities: number;
    depreciation: number;
    other: number;
    total: number;
  };
  netIncome: number;
}

// Response cache for GET requests
interface CachedResponse<T> {
  data: T;
  timestamp: number;
}

// API Service Class
class ApiService {
  private responseCache = new Map<string, CachedResponse<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private getHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      ...additionalHeaders,
    };
  }

  private getCacheKey(url: string, options: RequestInit): string {
    return `${options.method || 'GET'}:${url}`;
  }

  private getCachedResponse<T>(cacheKey: string): T | null {
    const cached = this.responseCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      console.log(`📦 Cache hit: ${cacheKey}`);
      return cached.data as T;
    }
    return null;
  }

  private setCachedResponse<T>(cacheKey: string, data: T): void {
    this.responseCache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    headers: Record<string, string> = {},
    retryCount = 0,
    maxRetries = 3
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: this.getHeaders({ ...headers }),
    };

    // Check cache for GET requests
    const isGetRequest = !options.method || options.method === 'GET';
    const cacheKey = this.getCacheKey(url, config);

    if (isGetRequest) {
      const cachedResponse = this.getCachedResponse<T>(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }
    }

    console.log(
      `🌐 API Request: ${options.method || 'GET'} ${url} (attempt ${retryCount + 1}/${maxRetries + 1})`
    );
    console.log(
      '🔑 Auth header present:',
      !!(config.headers as Record<string, string>)?.['Authorization']
    );

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Handle authentication errors
        if (response.status === 401) {
          console.log('🔄 401 Unauthorized error detected');

          // Clear cached user data
          localStorage.removeItem('cached_user');

          // Dispatch custom event to trigger auth refresh in AuthContext
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));

          // Only retry once on 401
          if (retryCount === 0) {
            console.log('⏳ Will retry request after auth refresh event...');
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return this.request<T>(endpoint, options, headers, retryCount + 1, maxRetries);
          }
        }

        // Handle 429 Rate Limit with exponential backoff
        if (response.status === 429 && retryCount < maxRetries) {
          const backoffMs = Math.min(2000 * Math.pow(2, retryCount), 30000); // Cap at 30s
          console.log(
            `⏰ 429 Rate Limited, backing off ${backoffMs}ms (attempt ${retryCount + 1}/${maxRetries})`
          );

          // Return stale cache if available
          if (isGetRequest) {
            const staleCache = this.responseCache.get(cacheKey);
            if (staleCache) {
              console.log(`📦 Returning stale cache due to rate limit: ${cacheKey}`);
              return staleCache.data as T;
            }
          }

          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          return this.request<T>(endpoint, options, headers, retryCount + 1, maxRetries);
        }

        // Handle 503 Service Unavailable with exponential backoff
        if (response.status === 503 && retryCount < maxRetries) {
          const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 10000); // Cap at 10s
          console.log(
            `⏰ 503 Service Unavailable, retrying in ${backoffMs}ms (attempt ${retryCount + 1}/${maxRetries})`
          );
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          return this.request<T>(endpoint, options, headers, retryCount + 1, maxRetries);
        }

        // For all other errors or max retries reached, return stale cache if available
        if (isGetRequest) {
          const staleCache = this.responseCache.get(cacheKey);
          if (staleCache) {
            console.warn(`⚠️ API error ${response.status}, returning stale cache: ${cacheKey}`);
            return staleCache.data as T;
          }
        }

        const errorMessage = `API request failed: ${response.status} ${response.statusText}`;
        if (retryCount >= maxRetries) {
          console.error(`❌ Max retries (${maxRetries}) reached for ${url}`);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Cache successful GET responses
      if (isGetRequest) {
        this.setCachedResponse(cacheKey, data);
      }

      return data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Handle network errors (fetch failed, CORS, etc.)
      if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        // Return stale cache for GET requests on network failure
        if (isGetRequest) {
          const staleCache = this.responseCache.get(cacheKey);
          if (staleCache) {
            console.warn(`⚠️ Network error, returning stale cache: ${cacheKey}`);
            return staleCache.data as T;
          }
        }

        if (retryCount < maxRetries) {
          const backoffMs = Math.min(1000 * Math.pow(2, retryCount), 10000);
          console.log(
            `⏰ Network error, retrying in ${backoffMs}ms (attempt ${retryCount + 1}/${maxRetries})`
          );
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          return this.request<T>(endpoint, options, headers, retryCount + 1, maxRetries);
        }
        console.error(`❌ Max retries (${maxRetries}) reached for ${url}`);
      }
      throw error;
    }
  }

  // Auth API calls
  async getCurrentUser(authHeaders: Record<string, string>): Promise<User> {
    return this.request('/api/auth/me', { method: 'GET' }, authHeaders);
  }

  async subscribeToDealAlerts(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      '/api/deal-alerts/subscribe',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
      {},
      0,
      0
    );
  }

  // Portfolio API calls
  async getPortfolios(authHeaders: Record<string, string>): Promise<Portfolio[]> {
    return this.request('/api/portfolios', { method: 'GET' }, authHeaders);
  }

  async createPortfolio(
    name: string,
    description: string,
    authHeaders: Record<string, string>
  ): Promise<Portfolio> {
    return this.request(
      '/api/portfolios',
      {
        method: 'POST',
        body: JSON.stringify({ name, description }),
      },
      authHeaders
    );
  }

  async deletePortfolio(portfolioId: string, authHeaders: Record<string, string>): Promise<void> {
    return this.request(
      `/api/portfolios/${portfolioId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // Property API calls
  async removeProperty(
    portfolioId: string,
    propertyId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/portfolios/${portfolioId}/properties/${propertyId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  async updateProperty(
    portfolioId: string,
    propertyId: string,
    authHeaders: Record<string, string>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analysisData?: any,
    notes?: string
  ): Promise<void> {
    return this.request(
      `/api/portfolios/${portfolioId}/properties/${propertyId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ analysisData, notes }),
      },
      authHeaders
    );
  }

  async addPropertyToPortfolio(
    portfolioId: string,
    address: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analysisData: any,
    notes: string,
    authHeaders: Record<string, string>
  ): Promise<Property> {
    return this.request(
      `/api/portfolios/${portfolioId}/properties`,
      {
        method: 'POST',
        body: JSON.stringify({ address, analysisData, notes }),
      },
      authHeaders
    );
  }

  // User API calls
  async getUsers(authHeaders: Record<string, string>): Promise<User[]> {
    return this.request('/api/users', { method: 'GET' }, authHeaders);
  }

  // Portfolio sharing API calls
  async getPortfolioShares(portfolioId: string, authHeaders: Record<string, string>) {
    return this.request(`/api/portfolios/${portfolioId}/shares`, { method: 'GET' }, authHeaders);
  }

  async sharePortfolio(
    portfolioId: string,
    email: string,
    permissionLevel: string,
    authHeaders: Record<string, string>
  ) {
    return this.request(
      `/api/portfolios/${portfolioId}/share`,
      {
        method: 'POST',
        body: JSON.stringify({ email, permission_level: permissionLevel }),
      },
      authHeaders
    );
  }

  async removePortfolioShare(shareId: string, authHeaders: Record<string, string>): Promise<void> {
    return this.request(
      `/api/portfolios/shares/${shareId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // Cache API calls
  async getCacheStats(authHeaders: Record<string, string>) {
    return this.request('/api/cache/stats', { method: 'GET' }, authHeaders);
  }

  async getCacheEntries(authHeaders: Record<string, string>) {
    return this.request('/api/cache/entries', { method: 'GET' }, authHeaders);
  }

  async clearCache(
    type: 'all' | 'expired' | 'selective',
    entryIds: string[] = [],
    authHeaders: Record<string, string>
  ): Promise<void> {
    let url = '/api/cache';
    let body = undefined;

    if (type === 'expired') {
      url += '?type=expired';
    } else if (type === 'selective' && entryIds.length > 0) {
      body = JSON.stringify({ entryIds });
    }

    return this.request(
      url,
      {
        method: 'DELETE',
        body,
      },
      authHeaders
    );
  }

  // Property analysis API calls
  async getPropertyDetails(
    address: string,
    authHeaders: Record<string, string> = {}
  ): Promise<any> {
    return this.request(
      `/api/property-details?address=${encodeURIComponent(address)}`,
      {},
      authHeaders
    );
  }

  async getRentEstimate(
    address: string,
    bedrooms: number,
    bathrooms: number,
    squareFootage: number,
    propertyType: string
  ): Promise<any> {
    return this.request('/api/rent-estimate', {
      method: 'POST',
      body: JSON.stringify({
        address,
        bedrooms,
        bathrooms,
        squareFootage,
        propertyType,
        provider: 'rentcast',
      }),
    });
  }

  async getTaxData(address: string, estimatedValue: number): Promise<any> {
    return this.request('/api/tax-data', {
      method: 'POST',
      body: JSON.stringify({
        address,
        estimatedValue,
      }),
    });
  }

  // Market Analysis API calls
  async getMarketAnalysis(marketArea: string): Promise<any> {
    return this.request(`/api/market-analysis/${encodeURIComponent(marketArea)}`);
  }

  async generateMarketAnalysis(
    marketArea: string,
    propertyAddress?: string,
    propertyType?: string,
    authHeaders?: Record<string, string>
  ): Promise<any> {
    return this.request(
      '/api/market-analysis/generate',
      {
        method: 'POST',
        body: JSON.stringify({ marketArea, propertyAddress, propertyType }),
      },
      authHeaders || {}
    );
  }

  async requestPropertyMarketAnalysis(
    propertyId: string,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/properties/${propertyId}/request-market-analysis`,
      {
        method: 'POST',
      },
      authHeaders
    );
  }

  async getPropertyWithMarketAnalysis(propertyId: string): Promise<any> {
    return this.request(`/api/properties/${propertyId}/market-analysis`);
  }

  async getPropertiesForMarketAnalysis(marketAnalysisId: string): Promise<any[]> {
    return this.request(`/api/market-analysis/${marketAnalysisId}/properties`);
  }

  async getAllMarketAnalyses(authHeaders: Record<string, string>): Promise<any[]> {
    return this.request('/api/market-analyses', { method: 'GET' }, authHeaders);
  }

  async deleteMarketAnalysis(
    marketAnalysisId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/market-analyses/${marketAnalysisId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  async refreshMarketAnalysis(
    marketAnalysisId: string,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/market-analyses/${marketAnalysisId}/refresh`,
      {
        method: 'POST',
      },
      authHeaders
    );
  }

  // Analysis Jobs API calls (async generation)
  async getAnalysisJob(jobId: string, authHeaders: Record<string, string>): Promise<any> {
    return this.request(`/api/analysis-jobs/${jobId}`, { method: 'GET' }, authHeaders);
  }

  async getUserAnalysisJobs(limit = 10, authHeaders: Record<string, string>): Promise<any[]> {
    return this.request(`/api/analysis-jobs?limit=${limit}`, { method: 'GET' }, authHeaders);
  }

  // ============================================================================
  // Owned Properties API calls
  // ============================================================================

  // Get all owned properties
  async getOwnedProperties(authHeaders: Record<string, string>): Promise<any[]> {
    return this.request('/api/owned-properties', { method: 'GET' }, authHeaders);
  }

  // Get portfolio summary
  async getPortfolioSummary(authHeaders: Record<string, string>): Promise<any> {
    return this.request('/api/owned-properties/summary', { method: 'GET' }, authHeaders);
  }

  // ============================================================================
  // Sourcing pipeline
  // ============================================================================

  async getSourcingLeads(
    filters: SourcingLeadFilters,
    authHeaders: Record<string, string>
  ): Promise<SourcingLeadListItem[]> {
    const params = new URLSearchParams();
    if (filters.freshness) params.set('freshness', filters.freshness);
    if (filters.tier) params.set('tier', filters.tier);
    if (filters.municipality) params.set('municipality', filters.municipality);
    if (typeof filters.minScore === 'number') params.set('minScore', String(filters.minScore));
    const query = params.toString();

    return this.request<SourcingLeadListItem[]>(
      `/api/sourcing/leads${query ? `?${query}` : ''}`,
      { method: 'GET' },
      authHeaders
    );
  }

  async getSourcingLead(
    leadId: string,
    authHeaders: Record<string, string>
  ): Promise<SourcingLeadDetail> {
    return this.request<SourcingLeadDetail>(
      `/api/sourcing/leads/${leadId}`,
      { method: 'GET' },
      authHeaders
    );
  }

  async getSourcingSources(authHeaders: Record<string, string>): Promise<SourcingSourcesOverview> {
    return this.request<SourcingSourcesOverview>(
      '/api/sourcing/sources',
      { method: 'GET' },
      authHeaders
    );
  }

  async getSourcingArtifactUrl(
    runId: string,
    authHeaders: Record<string, string>
  ): Promise<{ url: string; expires_in: number }> {
    return this.request<{ url: string; expires_in: number }>(
      `/api/sourcing/runs/${runId}/artifact-url`,
      { method: 'GET' },
      authHeaders
    );
  }

  async getSourcingLeadAnalyses(
    leadId: string,
    authHeaders: Record<string, string>
  ): Promise<SourcingLeadAnalysis[]> {
    return this.request<SourcingLeadAnalysis[]>(
      `/api/sourcing/leads/${leadId}/analyses`,
      { method: 'GET' },
      authHeaders
    );
  }

  async createSourcingLeadAnalysis(
    leadId: string,
    payload: { analysis_data: Record<string, unknown>; analyzed_address: string },
    authHeaders: Record<string, string>
  ): Promise<SourcingLeadAnalysis> {
    return this.request<SourcingLeadAnalysis>(
      `/api/sourcing/leads/${leadId}/analyses`,
      { method: 'POST', body: JSON.stringify(payload) },
      authHeaders
    );
  }

  // Get single owned property
  async getOwnedProperty(propertyId: string, authHeaders: Record<string, string>): Promise<any> {
    return this.request(`/api/owned-properties/${propertyId}`, { method: 'GET' }, authHeaders);
  }

  // Create owned property
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createOwnedProperty(data: any, authHeaders: Record<string, string>): Promise<any> {
    return this.request(
      '/api/owned-properties',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  // Update owned property
  async updateOwnedProperty(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  // Delete owned property
  async deleteOwnedProperty(
    propertyId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // Search tenants across all properties or a specific property
  async searchTenants(
    propertyId: string,
    filters: {
      search_term?: string;
      status?: string;
      include_historical?: boolean;
      date_from?: string;
      date_to?: string;
    },
    authHeaders: Record<string, string>
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.search_term) params.append('q', filters.search_term);
    if (filters.status) params.append('status', filters.status);
    if (filters.include_historical) params.append('include_historical', 'true');
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    const queryString = params.toString();
    const url = `/api/owned-properties/${propertyId}/tenants${queryString ? `?${queryString}` : ''}`;
    return this.request(url, { method: 'GET' }, authHeaders);
  }

  // ============================================================================
  // Co-Owners API calls
  // ============================================================================

  async getCoOwners(propertyId: string, authHeaders: Record<string, string>): Promise<any[]> {
    return this.request(
      `/api/owned-properties/${propertyId}/co-owners`,
      { method: 'GET' },
      authHeaders
    );
  }

  async createCoOwner(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/co-owners`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async updateCoOwner(
    propertyId: string,
    coOwnerId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/co-owners/${coOwnerId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async deleteCoOwner(
    propertyId: string,
    coOwnerId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/co-owners/${coOwnerId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // Co-Owner Invites
  async sendCoOwnerInvite(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/co-owner-invites`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async getPendingInvites(propertyId: string, authHeaders: Record<string, string>): Promise<any[]> {
    return this.request(
      `/api/owned-properties/${propertyId}/co-owner-invites`,
      { method: 'GET' },
      authHeaders
    );
  }

  async cancelInvite(
    propertyId: string,
    inviteId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/co-owner-invites/${inviteId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  async getUserPendingInvites(authHeaders: Record<string, string>): Promise<any[]> {
    return this.request(`/api/owned-properties/my-invites`, { method: 'GET' }, authHeaders);
  }

  async acceptInvite(token: string, authHeaders: Record<string, string>): Promise<any> {
    return this.request(
      `/api/owned-properties/invites/accept/${token}`,
      {
        method: 'POST',
      },
      authHeaders
    );
  }

  // ============================================================================
  // Property Debts API calls
  // ============================================================================

  async getPropertyDebts(propertyId: string, authHeaders: Record<string, string>): Promise<any[]> {
    return this.request(
      `/api/owned-properties/${propertyId}/debts`,
      { method: 'GET' },
      authHeaders
    );
  }

  async createDebt(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/debts`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async updateDebt(
    propertyId: string,
    debtId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/debts/${debtId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async deleteDebt(
    propertyId: string,
    debtId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/debts/${debtId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // ============================================================================
  // Property Units API calls
  // ============================================================================

  async getPropertyUnits(propertyId: string, authHeaders: Record<string, string>): Promise<any[]> {
    return this.request(
      `/api/owned-properties/${propertyId}/units`,
      { method: 'GET' },
      authHeaders
    );
  }

  async createUnit(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/units`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async updateUnit(
    propertyId: string,
    unitId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/units/${unitId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async deleteUnit(
    propertyId: string,
    unitId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/units/${unitId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // ============================================================================
  // Tenants API calls
  // ============================================================================

  async getTenants(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters?: any,
    authHeaders?: Record<string, string>
  ): Promise<any[]> {
    let url = `/api/owned-properties/${propertyId}/tenants`;
    const params = new URLSearchParams();
    if (filters?.unit_id) params.append('unit_id', filters.unit_id);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.include_historical) params.append('include_historical', 'true');
    if (filters?.q) params.append('q', filters.q);
    if (params.toString()) url += `?${params.toString()}`;
    return this.request(url, { method: 'GET' }, authHeaders || {});
  }

  async createTenant(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/tenants`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async updateTenant(
    propertyId: string,
    tenantId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/tenants/${tenantId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async deleteTenant(
    propertyId: string,
    tenantId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/tenants/${tenantId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // ============================================================================
  // Property Expenses API calls
  // ============================================================================

  async getExpenses(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters?: any,
    authHeaders?: Record<string, string>
  ): Promise<any[]> {
    let url = `/api/owned-properties/${propertyId}/expenses`;
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (params.toString()) url += `?${params.toString()}`;
    return this.request(url, { method: 'GET' }, authHeaders || {});
  }

  async createExpense(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/expenses`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async updateExpense(
    propertyId: string,
    expenseId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/expenses/${expenseId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async deleteExpense(
    propertyId: string,
    expenseId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/expenses/${expenseId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // ============================================================================
  // Property Income API calls
  // ============================================================================

  async getIncome(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters?: any,
    authHeaders?: Record<string, string>
  ): Promise<any[]> {
    let url = `/api/owned-properties/${propertyId}/income`;
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (params.toString()) url += `?${params.toString()}`;
    return this.request(url, { method: 'GET' }, authHeaders || {});
  }

  async createIncome(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/income`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async updateIncome(
    propertyId: string,
    incomeId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/income/${incomeId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async deleteIncome(
    propertyId: string,
    incomeId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/income/${incomeId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // ============================================================================
  // Maintenance Requests API calls
  // ============================================================================

  async getMaintenanceRequests(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters?: any,
    authHeaders?: Record<string, string>
  ): Promise<any[]> {
    let url = `/api/owned-properties/${propertyId}/maintenance`;
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.category) params.append('category', filters.category);
    if (params.toString()) url += `?${params.toString()}`;
    return this.request(url, { method: 'GET' }, authHeaders || {});
  }

  async createMaintenanceRequest(
    propertyId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/maintenance`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async updateMaintenanceRequest(
    propertyId: string,
    requestId: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/owned-properties/${propertyId}/maintenance/${requestId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  async deleteMaintenanceRequest(
    propertyId: string,
    requestId: string,
    authHeaders: Record<string, string>
  ): Promise<void> {
    return this.request(
      `/api/owned-properties/${propertyId}/maintenance/${requestId}`,
      {
        method: 'DELETE',
      },
      authHeaders
    );
  }

  // ============================================================================
  // Onboarding Survey API calls
  // ============================================================================

  // Get current user's survey
  async getUserSurvey(authHeaders: Record<string, string>): Promise<any> {
    return this.request('/api/surveys/me', { method: 'GET' }, authHeaders);
  }

  // Create or update survey
  async saveSurvey(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    surveyData: any,
    currentStep: number,
    status: 'in_progress' | 'completed' | 'skipped',
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      '/api/surveys',
      {
        method: 'POST',
        body: JSON.stringify({
          survey_data: surveyData,
          current_step: currentStep,
          status,
        }),
      },
      authHeaders
    );
  }

  // Skip survey
  async skipSurvey(authHeaders: Record<string, string>): Promise<any> {
    return this.request(
      '/api/surveys/skip',
      {
        method: 'POST',
      },
      authHeaders
    );
  }

  // Complete survey
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async completeSurvey(surveyData: any, authHeaders: Record<string, string>): Promise<any> {
    return this.request(
      '/api/surveys/complete',
      {
        method: 'POST',
        body: JSON.stringify({ survey_data: surveyData }),
      },
      authHeaders
    );
  }

  // Admin: Get all surveys
  async getAdminSurveys(
    options: { status?: string; limit?: number; offset?: number },
    authHeaders: Record<string, string>
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    const queryString = params.toString();
    return this.request(
      `/api/admin/surveys${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
      authHeaders
    );
  }

  // Admin: Get survey statistics
  async getAdminSurveyStats(authHeaders: Record<string, string>): Promise<any> {
    return this.request('/api/admin/surveys/stats', { method: 'GET' }, authHeaders);
  }

  // Admin: Get user survey summaries
  async getAdminUserSurveySummaries(
    options: { search?: string; status?: string; role?: string; limit?: number; offset?: number },
    authHeaders: Record<string, string>
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (options.search) params.append('search', options.search);
    if (options.status) params.append('status', options.status);
    if (options.role) params.append('role', options.role);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    const queryString = params.toString();
    return this.request(
      `/api/admin/surveys/users${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
      authHeaders
    );
  }

  // Admin: Get specific user's survey
  async getAdminUserSurvey(userId: string, authHeaders: Record<string, string>): Promise<any> {
    return this.request(`/api/admin/surveys/user/${userId}`, { method: 'GET' }, authHeaders);
  }

  // Admin: Trigger surveys for multiple users
  async triggerSurveys(
    request: { userIds: string[]; surveyType?: string; campaignName?: string },
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      '/api/admin/surveys/trigger',
      {
        method: 'POST',
        body: JSON.stringify(request),
      },
      authHeaders
    );
  }

  // Admin: Get users eligible for survey triggering
  async getEligibleUsers(
    options: {
      excludeCompleted?: boolean;
      surveyType?: string;
      role?: string;
      investmentCapacity?: string;
      experienceLevel?: string;
    },
    authHeaders: Record<string, string>
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (options.excludeCompleted) params.append('excludeCompleted', 'true');
    if (options.surveyType) params.append('surveyType', options.surveyType);
    if (options.role) params.append('role', options.role);
    if (options.investmentCapacity) params.append('investmentCapacity', options.investmentCapacity);
    if (options.experienceLevel) params.append('experienceLevel', options.experienceLevel);
    const queryString = params.toString();
    return this.request(
      `/api/admin/surveys/eligible-users${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
      authHeaders
    );
  }

  // Admin: Get survey history
  async getSurveyHistory(
    options: { userId?: string; surveyType?: string; limit?: number; offset?: number },
    authHeaders: Record<string, string>
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (options.userId) params.append('userId', options.userId);
    if (options.surveyType) params.append('surveyType', options.surveyType);
    if (options.limit) params.append('limit', options.limit.toString());
    if (options.offset) params.append('offset', options.offset.toString());
    const queryString = params.toString();
    return this.request(
      `/api/admin/surveys/history${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' },
      authHeaders
    );
  }

  // ============================================================================
  // Bulk Import API calls
  // ============================================================================

  // Check if user can bulk import
  async canBulkImport(authHeaders: Record<string, string>): Promise<{ can_bulk_import: boolean }> {
    return this.request('/api/bulk-import/can-bulk-import', { method: 'GET' }, authHeaders);
  }

  // Create a bulk import job
  async createBulkImportJob(
    data: {
      import_type: 'deal_analysis' | 'owned_property';
      target_portfolio_id?: string;
      name?: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      items?: Array<{ address: string; raw_input?: any }>;
      addresses_text?: string;
    },
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      '/api/bulk-import/jobs',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      authHeaders
    );
  }

  // Get user's bulk import jobs
  async getBulkImportJobs(authHeaders: Record<string, string>, limit?: number): Promise<any[]> {
    const url = limit ? `/api/bulk-import/jobs?limit=${limit}` : '/api/bulk-import/jobs';
    return this.request(url, { method: 'GET' }, authHeaders);
  }

  // Get a specific bulk import job
  async getBulkImportJob(jobId: string, authHeaders: Record<string, string>): Promise<any> {
    return this.request(`/api/bulk-import/jobs/${jobId}`, { method: 'GET' }, authHeaders);
  }

  // Start processing a bulk import job
  async startBulkImportJob(jobId: string, authHeaders: Record<string, string>): Promise<any> {
    return this.request(`/api/bulk-import/jobs/${jobId}/start`, { method: 'POST' }, authHeaders);
  }

  // Cancel a bulk import job
  async cancelBulkImportJob(jobId: string, authHeaders: Record<string, string>): Promise<any> {
    return this.request(`/api/bulk-import/jobs/${jobId}/cancel`, { method: 'POST' }, authHeaders);
  }

  // Admin: Get all users with bulk import permissions
  async getBulkImportPermissions(authHeaders: Record<string, string>): Promise<any[]> {
    return this.request('/api/bulk-import/admin/permissions', { method: 'GET' }, authHeaders);
  }

  // Admin: Update a user's bulk import permission
  async updateBulkImportPermission(
    userId: string,
    canBulkImport: boolean,
    authHeaders: Record<string, string>
  ): Promise<any> {
    return this.request(
      `/api/bulk-import/admin/permissions/${userId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ can_bulk_import: canBulkImport }),
      },
      authHeaders
    );
  }

  // Admin: Get all bulk import jobs
  async getAllBulkImportJobs(authHeaders: Record<string, string>, limit?: number): Promise<any[]> {
    const url = limit
      ? `/api/bulk-import/admin/jobs?limit=${limit}`
      : '/api/bulk-import/admin/jobs';
    return this.request(url, { method: 'GET' }, authHeaders);
  }

  // ============================================================================
  // USAGE TRACKING & SUBSCRIPTIONS
  // ============================================================================

  // Get current user's usage metrics
  async getUserUsageMetrics(authHeaders: Record<string, string>): Promise<any> {
    return this.request('/api/usage/metrics', { method: 'GET' }, authHeaders);
  }

  // Admin: Get usage events
  async getUsageEvents(
    authHeaders: Record<string, string>,
    params?: {
      startDate?: string;
      endDate?: string;
      eventType?: string;
    }
  ): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.eventType) queryParams.append('eventType', params.eventType);

    const url = `/api/admin/usage-events${queryParams.toString() ? `?${queryParams}` : ''}`;
    return this.request(url, { method: 'GET' }, authHeaders);
  }

  // Create Stripe customer
  async createStripeCustomer(authHeaders: Record<string, string>): Promise<{ customerId: string }> {
    return this.request(
      '/api/stripe/create-customer',
      {
        method: 'POST',
      },
      authHeaders
    );
  }

  // Create Stripe checkout session
  async createCheckoutSession(
    priceId: string,
    authHeaders: Record<string, string>
  ): Promise<{ url: string }> {
    return this.request(
      '/api/stripe/create-checkout',
      {
        method: 'POST',
        body: JSON.stringify({ priceId }),
      },
      authHeaders
    );
  }

  // Cancel subscription
  async cancelSubscription(authHeaders: Record<string, string>): Promise<{ message: string }> {
    return this.request(
      '/api/stripe/cancel-subscription',
      {
        method: 'POST',
      },
      authHeaders
    );
  }

  // Reactivate subscription
  async reactivateSubscription(authHeaders: Record<string, string>): Promise<{ message: string }> {
    return this.request(
      '/api/stripe/reactivate-subscription',
      {
        method: 'POST',
      },
      authHeaders
    );
  }

  // Get subscription details
  async getSubscriptionDetails(authHeaders: Record<string, string>): Promise<{
    tier: string;
    status: string;
    startDate: string | null;
    endDate: string | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    stripeSubscription: any;
    paymentMethod: {
      brand: string;
      last4: string;
      expMonth: number;
      expYear: number;
    } | null;
  }> {
    return this.request(
      '/api/stripe/subscription-details',
      {
        method: 'GET',
      },
      authHeaders
    );
  }

  // Get billing history
  async getInvoices(authHeaders: Record<string, string>): Promise<{
    invoices: Array<{
      id: string;
      date: number;
      amount: number;
      status: string;
      invoicePdf: string | null;
      hostedInvoiceUrl: string | null;
    }>;
  }> {
    return this.request(
      '/api/stripe/invoices',
      {
        method: 'GET',
      },
      authHeaders
    );
  }

  // Update subscription (upgrade/downgrade)
  async updateSubscription(
    priceId: string,
    authHeaders: Record<string, string>
  ): Promise<{ message: string }> {
    return this.request(
      '/api/stripe/update-subscription',
      {
        method: 'POST',
        body: JSON.stringify({ priceId }),
      },
      authHeaders
    );
  }

  // Transaction API methods
  async getTransactions(
    userId: string,
    filters: {
      startDate?: string;
      endDate?: string;
      category?: string;
      status?: string;
      propertyId?: string;
    },
    authHeaders: Record<string, string> = {}
  ): Promise<Transaction[]> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.category) params.append('category', filters.category);
    if (filters.status) params.append('status', filters.status);
    if (filters.propertyId) params.append('propertyId', filters.propertyId);

    const endpoint = `/api/transactions?${params.toString()}`;
    return this.request<Transaction[]>(endpoint, { method: 'GET' }, authHeaders);
  }

  async createTransaction(
    transaction: Transaction,
    authHeaders: Record<string, string> = {}
  ): Promise<Transaction> {
    return this.request<Transaction>(
      '/api/transactions',
      {
        method: 'POST',
        body: JSON.stringify(transaction),
      },
      authHeaders
    );
  }

  async updateTransaction(
    transactionId: string,
    updates: Partial<Transaction>,
    authHeaders: Record<string, string> = {}
  ): Promise<Transaction> {
    return this.request<Transaction>(
      `/api/transactions/${transactionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
      },
      authHeaders
    );
  }

  async deleteTransaction(
    transactionId: string,
    userId: string,
    authHeaders: Record<string, string> = {}
  ): Promise<void> {
    return this.request<void>(
      `/api/transactions/${transactionId}?userId=${userId}`,
      { method: 'DELETE' },
      authHeaders
    );
  }

  // Transaction Allocation API methods
  async getTransactionAllocations(
    transactionId: string,
    authHeaders: Record<string, string> = {}
  ): Promise<TransactionAllocation[]> {
    return this.request<TransactionAllocation[]>(
      `/api/transactions/${transactionId}/allocations`,
      { method: 'GET' },
      authHeaders
    );
  }

  async createTransactionAllocation(
    transactionId: string,
    allocation: Omit<TransactionAllocation, 'id' | 'transaction_id' | 'created_at' | 'updated_at'>,
    authHeaders: Record<string, string> = {}
  ): Promise<TransactionAllocation> {
    return this.request<TransactionAllocation>(
      `/api/transactions/${transactionId}/allocations`,
      {
        method: 'POST',
        body: JSON.stringify(allocation),
      },
      authHeaders
    );
  }

  async updateTransactionAllocation(
    allocationId: string,
    updates: Partial<TransactionAllocation>,
    authHeaders: Record<string, string> = {}
  ): Promise<TransactionAllocation> {
    return this.request<TransactionAllocation>(
      `/api/transactions/allocations/${allocationId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates),
      },
      authHeaders
    );
  }

  async deleteTransactionAllocation(
    allocationId: string,
    authHeaders: Record<string, string> = {}
  ): Promise<void> {
    return this.request<void>(
      `/api/transactions/allocations/${allocationId}`,
      { method: 'DELETE' },
      authHeaders
    );
  }

  // Allocation Template API methods
  async getAllocationTemplates(
    authHeaders: Record<string, string> = {}
  ): Promise<AllocationTemplate[]> {
    return this.request<AllocationTemplate[]>(
      '/api/transactions/templates',
      { method: 'GET' },
      authHeaders
    );
  }

  async createAllocationTemplate(
    template: Omit<AllocationTemplate, 'id' | 'created_at' | 'updated_at'>,
    authHeaders: Record<string, string> = {}
  ): Promise<AllocationTemplate> {
    return this.request<AllocationTemplate>(
      '/api/transactions/templates',
      {
        method: 'POST',
        body: JSON.stringify(template),
      },
      authHeaders
    );
  }

  async deleteAllocationTemplate(
    templateId: string,
    authHeaders: Record<string, string> = {}
  ): Promise<void> {
    return this.request<void>(
      `/api/transactions/templates/${templateId}`,
      { method: 'DELETE' },
      authHeaders
    );
  }

  // ===================
  // Reports API
  // ===================

  async getProfitLossReport(
    startDate: string,
    endDate: string,
    propertyId?: string,
    groupBy: 'month' | 'quarter' | 'year' = 'month',
    authHeaders: Record<string, string> = {}
  ): Promise<ProfitLossReport> {
    const params = new URLSearchParams({
      startDate,
      endDate,
      groupBy,
    });
    if (propertyId) {
      params.append('propertyId', propertyId);
    }

    return this.request<ProfitLossReport>(
      `/api/reports/profit-loss?${params}`,
      { method: 'GET' },
      authHeaders
    );
  }

  async getCashFlowReport(
    startDate: string,
    endDate: string,
    propertyId?: string,
    groupBy: 'month' | 'quarter' | 'year' = 'month',
    authHeaders: Record<string, string> = {}
  ): Promise<CashFlowReport> {
    const params = new URLSearchParams({
      startDate,
      endDate,
      groupBy,
    });
    if (propertyId) {
      params.append('propertyId', propertyId);
    }

    return this.request<CashFlowReport>(
      `/api/reports/cash-flow?${params}`,
      { method: 'GET' },
      authHeaders
    );
  }

  async getScheduleEReport(
    year: number,
    propertyId: string,
    authHeaders: Record<string, string> = {}
  ): Promise<ScheduleEReport> {
    const params = new URLSearchParams({
      year: year.toString(),
      propertyId,
    });

    return this.request<ScheduleEReport>(
      `/api/reports/schedule-e?${params}`,
      { method: 'GET' },
      authHeaders
    );
  }
}

// Export singleton instance
export const apiService = new ApiService();
